import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  ScrollRestoration,
  useNavigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import { Drawer } from "antd";

import Authentication from "./Authentication";
import Sidebar from "./Components/Sidebar/Sidebar";
import Navbar from "./Components/Navbar/Navbar";
import { BillingProvider } from "./Context/BillingContext";
import PaymentStatusBanner from "./Components/CampaignBilling/PaymentStatusBanner";
import {
  getProfile,
  restoreAuthSession,
  setAdminRole,
  setCanManageAdminTeam,
  setIsAdmin,
} from "./module/features/Authentication";
import { getToken } from "./module/features/Spotify";
import { handleSessionExpired, isSessionExpiredPayload } from "./utils/sessionExpiry";
import "react-toastify/dist/ReactToastify.css";
import { getArtistCampaigns } from "./module/features/Artist";
import { DrawerOpenIcon } from "./Assets/Icons";
import AdminSidebar from "./Pages/AdminPanel/Components/AdminSidebar/AdminSidebar";
import AdminNavbar from "./Pages/AdminPanel/Components/AdminNavbar/Navbar";
import Loader from "./Components/Loader/Loader";

// Route-level pages are code-split: each is only fetched when its route is visited.
const Home = React.lazy(() => import("./Pages/Home/Home"));
const BillingDashboard = React.lazy(() => import("./Components/CampaignBilling/CampaignBilling"));
const AddTrack = React.lazy(() => import("./Pages/Create-Campaign/AddTrack/AddTrack"));
const CreateCampaignLayout = React.lazy(() => import("./Pages/Create-Campaign/CreateCampaignLayout"));
const SetBudget = React.lazy(() => import("./Pages/Create-Campaign/AddTrack/SetBudget"));
const ReviewCampaign = React.lazy(() => import("./Pages/Create-Campaign/AddTrack/ReviewCampaign"));
const ViewCampaign = React.lazy(() => import("./Pages/view-campaign/ViewCampaign"));
const Drafts = React.lazy(() => import("./Pages/drafts/Drafts"));
const AdminCampaign = React.lazy(() => import("./Pages/AdminPanel/AdminPanel.js"));
const CampaignExcelView = React.lazy(() => import("./Pages/AdminPanel/CampaignExcelView.js"));
const AdminDashboard = React.lazy(() => import("./Pages/AdminPanel/Components/AdminDashboard/AdminDashboard.jsx"));
const CuratorsListing = React.lazy(() => import("./Pages/AdminPanel/Components/CuratorsListing/CuratorsListing.js"));
const ArtistsListing = React.lazy(() => import("./Pages/AdminPanel/Components/ArtistsListing/ArtistsListing.jsx"));
const AdminSettings = React.lazy(() => import("./Pages/AdminPanel/Components/AdminSettings/AdminSettings.js"));
const PayoutQueue = React.lazy(() => import("./Pages/AdminPanel/Components/PayoutQueue/PayoutQueue.jsx"));
const AccountOverview = React.lazy(() => import("./Pages/AccountOverview/AccountOverview"));
const Callback = React.lazy(() => import("./Pages/Callback/Callback"));
const CompleteProfile = React.lazy(() => import("./Pages/CompleteProfile/CompleteProfile"));
const Landing = React.lazy(() => import("./Pages/Landing/Landing"));
const PrivacyPolicy2 = React.lazy(() => import("./Pages/policies/privacy-policy-v2.js"));
const Condition2 = React.lazy(() => import("./Pages/policies/terms-and-condition-v2.js"));
const NotFound = React.lazy(() => import("./Pages/NotFound/NotFound"));
const TestTailwind = React.lazy(() => import("./Pages/TestTailwind/TestTailwind"));
const AdminCreateCampaign = React.lazy(() => import("./Pages/Admin/CreateCampaign/AdminCreateCampaign"));

const LayoutOutlet = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth0User = useSelector((e) => e.authenticationSlice.auth0User);
  const [open, setOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem("songplace_sidebar_collapsed") === "true";
  });

  const onClose = () => {
    setOpen(false);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("songplace_sidebar_collapsed", String(next));
      return next;
    });
  };

  useEffect(() => {
    dispatch(getToken());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only perform this check after auth state is fully loaded
    if (auth0User === null) {
      // Auth state is still loading, don't redirect yet
      return;
    }

    // Check if user is authenticated
    const isAuthenticated =
      !!auth0User && !!localStorage.getItem("access_token");

    // If not authenticated, redirect to landing page
    if (!isAuthenticated) {
      navigate("/");
    }
  }, [auth0User, navigate]);

  // Profile completion is suggested once after login (Callback), not forced on navigation.

  return (
    <>
      <ScrollRestoration />
      <div className="innerLayout artistAppShell light">
        <div className="layoutBody">
          <div className={`sideBar ${sidebarCollapsed ? "collapsed" : ""}`}>
            <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
          </div>
          <div className={`content ${sidebarCollapsed ? "expanded" : ""}`}>
            <div className="navbarDiv">
              <button
                type="button"
                className="drawerOpenBtn"
                onClick={() => setOpen(true)}
                aria-label="Open navigation menu"
              >
                <DrawerOpenIcon />
                <span>Menu</span>
              </button>
              <Navbar hideLogo={true} />
              <Drawer
                title={false}
                placement={"left"}
                closable={false}
                onClose={onClose}
                open={open}
                key={"left"}
                className="sideBarDrawer artistSideBarDrawer"
                rootClassName="sideBarDrawerRoot artistSideBarDrawerRoot light"
              >
                <Sidebar onClose={onClose} />
              </Drawer>
            </div>
            <div className="innerContent light">
              <div className="artistContentCanvas mx-auto w-full max-w-[1320px] px-4 py-6 md:px-8 md:py-10">
                <PaymentStatusBanner />
                <Authentication>
                  <React.Suspense
                    fallback={
                      <div className="flex justify-center items-center py-20">
                        <Loader />
                      </div>
                    }
                  >
                    <Outlet />
                  </React.Suspense>
                </Authentication>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const AdminOutlet = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth0User = useSelector((e) => e.authenticationSlice.auth0User);
  const isAdmin = useSelector((e) => e.authenticationSlice.isAdmin);
  const [open, setOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem("songplace_sidebar_collapsed") === "true";
  });

  const onClose = () => {
    setOpen(false);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("songplace_sidebar_collapsed", String(next));
      return next;
    });
  };

  useEffect(() => {
    dispatch(getToken());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only perform this check after auth state is fully loaded
    if (auth0User === null) {
      // Auth state is still loading, don't redirect yet
      return;
    }

    // Check if user is authenticated and is an admin
    const isAuthenticated =
      !!auth0User && !!localStorage.getItem("access_token");

    // If not authenticated, redirect to landing page
    if (!isAuthenticated) {
      navigate("/");
      return;
    }

    // If not an admin, redirect to landing page
    if (isAuthenticated && !isAdmin) {
      navigate("/");
      return;
    }
  }, [auth0User, isAdmin, navigate]);

  return (
    <>
      <div className="innerLayout">
        <div className="layoutBody">
          <div className={`sideBar ${sidebarCollapsed ? "collapsed" : ""}`}>
            <AdminSidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
          </div>
          <div className={`content ${sidebarCollapsed ? "expanded" : ""}`}>
            <div className="navbarDiv">
              <button
                type="button"
                className="drawerOpenBtn"
                onClick={() => setOpen(true)}
                aria-label="Open navigation menu"
              >
                <DrawerOpenIcon />
                <span>Menu</span>
              </button>
              <AdminNavbar hideLogo={true} />
              <Drawer
                title={false}
                placement={"left"}
                closable={false}
                onClose={onClose}
                open={open}
                key={"left"}
                className="sideBarDrawer"
                rootClassName="sideBarDrawerRoot"
              >
                <AdminSidebar onClose={onClose} />
              </Drawer>
            </div>
            <div className="innerContent">
              <Authentication>
                <React.Suspense
                  fallback={
                    <div className="flex justify-center items-center py-20">
                      <Loader />
                    </div>
                  }
                >
                  <Outlet />
                </React.Suspense>
              </Authentication>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

function LayoutComp() {
  const dispatch = useDispatch();
  const user = useSelector((e) => e.authenticationSlice.user);
  const isAdmin = useSelector((e) => e.authenticationSlice.isAdmin);
  const [adminStatusLoaded, setAdminStatusLoaded] = useState(false);
  const lastFocusSyncRef = useRef(0);

  const syncSessionState = async () => {
    if (!localStorage.getItem("access_token")) {
      return;
    }

    try {
      const sessionData = await dispatch(restoreAuthSession()).unwrap();
      if (sessionData?.isAdmin) {
        localStorage.setItem("userMetadata", "true");
        if (sessionData?.adminRole) {
          localStorage.setItem("adminRole", sessionData.adminRole);
        } else {
          localStorage.removeItem("adminRole");
        }
      } else {
        localStorage.removeItem("userMetadata");
        localStorage.removeItem("adminRole");
      }
    } catch (error) {
      // Only a rejected token ends the session; network or server errors keep it.
      if (isSessionExpiredPayload(error?.status, error)) {
        handleSessionExpired();
      }
    }
  };

  useEffect(() => {
    if (user) {
      dispatch(getArtistCampaigns());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  useEffect(() => {
    const hydrateAdminStatus = async () => {
      const storedIsAdmin = localStorage.getItem("userMetadata");
      if (storedIsAdmin !== null) {
        dispatch(setIsAdmin(storedIsAdmin === "true"));
      }
      dispatch(setAdminRole(localStorage.getItem("adminRole")));
      dispatch(setCanManageAdminTeam(false));

      if (localStorage.getItem("access_token")) {
        await syncSessionState();
      }

      setAdminStatusLoaded(true);
    };

    hydrateAdminStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (adminStatusLoaded) {
      dispatch(getProfile());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminStatusLoaded]);

  useEffect(() => {
    if (!adminStatusLoaded || !localStorage.getItem("access_token")) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      syncSessionState();
    }, 60000);

    const handleWindowFocus = () => {
      const now = Date.now();
      if (now - lastFocusSyncRef.current < 10000) {
        return;
      }
      lastFocusSyncRef.current = now;
      syncSessionState();
    };

    window.addEventListener("focus", handleWindowFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleWindowFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminStatusLoaded]);

  // Route table only branches on isAdmin; everything else is a stable module-level import.
  const router = useMemo(() => createBrowserRouter([
    {
      path: "/",
      Component: Landing,
    },
    {
      path: "/landing",
      Component: Landing,
    },
    {
      path: "/test-tailwind",
      Component: TestTailwind,
    },
    {
      path: "/privacy-policy",
      Component: PrivacyPolicy2,
    },
    {
      path: "/terms-and-condition",
      Component: Condition2,
    },
    {
      path: "/callback",
      Component: Callback,
    },
    // Admin routes
    ...(isAdmin ? [
      {
        element: <AdminOutlet />,
        children: [
          {
            path: "/dashboard",
            Component: AdminDashboard,
          },
          {
            path: "/dashboard/campaigns",
            Component: CampaignExcelView,
          },
          {
            path: "/dashboard/campaigns/card",
            Component: AdminCampaign,
          },
          {
            path: "/dashboard/campaigns/table",
            Component: CampaignExcelView,
          },
          {
            path: "/dashboard/campaign-search",
            element: <Navigate to="/dashboard/campaigns" replace />,
          },
          {
            path: "/dashboard/curators",
            Component: CuratorsListing,
          },
          {
            path: "/dashboard/artists",
            Component: ArtistsListing,
          },
          {
            path: "/dashboard/settings",
            Component: AdminSettings,
          },
          {
            path: "/dashboard/payouts",
            Component: PayoutQueue,
          },
          {
            path: "/dashboard/account-overview",
            Component: AccountOverview,
          },
          {
            path: "/dashboard/complete-profile",
            Component: CompleteProfile,
          },
          {
            path: "/dashboard/create-campaign",
            Component: AdminCreateCampaign,
          },
        ],
      },
    ] : [
      // Non-admin routes  
      {
        element: <LayoutOutlet />,
        children: [
          {
            path: "/dashboard",
            Component: Home,
          },
          {
            path: "/dashboard/view-campaigns",
            Component: ViewCampaign,
          },
          {
            path: "/dashboard/drafts",
            Component: Drafts,
          },
          {
            path: "/dashboard/billing",
            Component: BillingDashboard,
          },
          {
            path: "/dashboard/account-overview",
            Component: AccountOverview,
          },
          {
            path: "/dashboard/complete-profile",
            Component: CompleteProfile,
          },
          {
            element: <CreateCampaignLayout />,
            children: [
              {
                path: "/create-campaign/add-track",
                Component: AddTrack,
              },
              {
                path: "/create-campaign/set-budget-duration",
                Component: SetBudget,
              },
              {
                path: "/create-campaign/review-campaign",
                Component: ReviewCampaign,
              },
            ],
          },
        ],
      },
    ]),
    {
      path: "*",
      Component: NotFound,
    },
  ]), [isAdmin]);

  // Don't render router until admin status is determined
  if (!adminStatusLoaded) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    )
  }

  // Check if current route should not have layout wrapper
  const isMainLandingRoute = window.location.pathname === '/';
  const isLandingRoute = window.location.pathname === '/landing';
  const isPrivacyPolicyRoute = window.location.pathname === '/privacy-policy';
  const isTermsAndConditionsRoute = window.location.pathname === '/terms-and-condition';
  const isCuratorInviteRoute = false;
  const shouldHideLayout = isMainLandingRoute || isLandingRoute || isPrivacyPolicyRoute || isTermsAndConditionsRoute || isCuratorInviteRoute;

  return (
    <div className={shouldHideLayout ? "" : "layout"}>
      <BillingProvider>
        <React.Suspense
          fallback={
            <div className="flex justify-center items-center h-screen">
              <Loader />
            </div>
          }
        >
          <RouterProvider router={router} />
        </React.Suspense>
      </BillingProvider>
      <ToastContainer />
    </div>
  );
}

export default LayoutComp;
